import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'

type Block = {
  type: 'text' | 'quote'
  content: string
}

export function PhilosophyGrid() {
  const { t } = useTranslation('home')
  const shouldReduceMotion = useReducedMotion()
  const ease = [0.22, 1, 0.36, 1] as const

  const blocks = (t('philosophy.blocks', { returnObjects: true }) ?? []) as Block[]

  return (
    <section
      className="bg-paper px-4 py-[clamp(4rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby="philosophy-heading"
    >
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 30 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease }}
          className="mb-12 lg:mb-16"
        >
          <h2
            id="philosophy-heading"
            className="font-display text-[clamp(2rem,5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink"
          >
            {t('philosophy.title')}
          </h2>
        </motion.div>

        {/* Content grid */}
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Image */}
          <div className="self-start lg:col-span-5 lg:sticky lg:top-32">
            <div className="relative overflow-hidden rounded-bubble">
              <img
                src="/media/filosofia.jpg"
                alt={t('philosophy.imageAlt')}
                className="aspect-[4/5] w-full object-cover sm:aspect-square lg:aspect-[4/5]"
                loading="lazy"
              />
            </div>
          </div>

          {/* Text manifesto */}
          <div className="flex flex-col gap-12 lg:col-span-7">
            {blocks.map((block, index) => (
              <motion.div
                key={index}
                initial={shouldReduceMotion ? undefined : { opacity: 0, y: 30 }}
                whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{
                  duration: 0.6,
                  ease,
                  delay: shouldReduceMotion ? 0 : 0.1 * index,
                }}
              >
                {block.type === 'quote' ? (
                  <blockquote className="border-l-[6px] border-maia py-2 pl-6 font-display text-[clamp(1.25rem,2.5vw,1.75rem)] font-semibold leading-snug text-ink">
                    {block.content}
                  </blockquote>
                ) : (
                  <p className="text-lg leading-relaxed text-muted">
                    {block.content}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Signature */}
        <motion.div
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 30 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease }}
          className="mt-16 flex justify-end lg:mt-24"
        >
          <p className="font-display text-lg font-medium text-ink">
            <span className="mr-2 text-maia">—</span>
            {t('philosophy.quoteAuthor')}
          </p>
        </motion.div>
      </div>
    </section>
  )
}
